/**
 * Step 10: Review & Run
 * Read-only summary of all wizard inputs with Edit links and Create Projection button.
 */

import { useCallback, useState } from 'react';
import type { AllStepFormData, HoldingData, AdditionalPensionData, PropertyItem } from '../../types/wizard';
import { formatCurrency, planYearToCalendar } from '../../utils/formatters';
import { stripLegacyFields } from './stripLegacyFields';
import { priorIncomeAppliesToPlanYear } from '../../utils/taxYear';
import { deriveTotalPriorOtherIncome } from '../../utils/priorIncomeSeed';
import { blendedEquityPctFromHoldings } from '../../utils/allocation';
import { DEFAULT_COMMUTATION_FACTOR } from '../../utils/dbPensionMath';
import { summariseAnnuityPurchase } from './annuityPurchaseHelpers';
import { isFeatureEnabled } from '../../config/features';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP10_TOUR } from '../TourGuide/tourSteps';
import './StepReview.css';

interface StepReviewProps {
  formData: Partial<AllStepFormData>;
  onEditStep: (step: number) => void;
  onViewProjection: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  isAlternative?: boolean;
  projectionReady?: boolean;
  autoProjectionError?: string | null;
}

const STRATEGY_LABELS: Record<string, string> = {
  TAX_OPTIMISED: 'Tax-Optimised',
  ISA_FIRST: 'ISA First',
  SIPP_FIRST: 'SIPP First',
  PROPORTIONAL: 'Proportional',
};

const BAND_FILL_LABELS: Record<string, string> = {
  PA_ONLY: 'Personal Allowance Only',
  PA_AND_BASIC: 'PA + Basic Rate',
  PA_AND_HIGHER: 'PA + Basic + Higher Rate',
};

const ISA_SHELTERING_LABELS: Record<string, string> = {
  AEA_AWARE: 'AEA-Aware',
  FULL: 'Full Shelter',
};

const CARE_TYPE_LABELS: Record<string, string> = {
  HOME: 'Home',
  RESIDENTIAL: 'Residential',
  NURSING: 'Nursing',
};

function fmt(value: number | undefined): string {
  if (value == null) return '-';
  return formatCurrency(value, true);
}

function pct(value: number | undefined): string {
  if (value == null) return '-';
  return `${value}%`;
}

function SectionCard({ title, step, onEdit, dataTour, children }: Readonly<{
  title: string;
  step: number;
  onEdit: () => void;
  dataTour?: string;
  children: React.ReactNode;
}>) {
  return (
    <div className="review-section" data-tour={dataTour}>
      <div className="review-section-header">
        <h3 className="review-section-title">
          {title}
          <span className="review-section-step">Step {step}</span>
        </h3>
        <button type="button" className="review-edit-btn" onClick={onEdit} aria-label={`Edit ${title}`}>
          Edit
        </button>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, full }: Readonly<{ label: string; value: React.ReactNode; full?: boolean }>) {
  return (
    <div className={`review-field${full ? ' review-field--full' : ''}`}>
      <span className="review-label">{label}</span>
      <span className="review-value">{value ?? <span className="review-value--muted">Not set</span>}</span>
    </div>
  );
}

function getHoldingsSummary(holdings: Record<string, HoldingData[]> | undefined, personIndex: number): { sipp: number; isa: number; gia: number; individualBondCount: number } {
  const items = holdings?.[`person${personIndex}`] ?? [];
  let sipp = 0, isa = 0, gia = 0, individualBondCount = 0;
  for (const h of items) {
    if (h.accountType === 'SIPP') sipp += h.currentValue;
    else if (h.accountType === 'ISA') isa += h.currentValue;
    else gia += h.currentValue;
    if (h.bondDetails) individualBondCount++;
  }
  return { sipp, isa, gia, individualBondCount };
}

/**
 * Pension line for the review: count, annual income after any pension given
 * up for cash (today's money, before the engine's early/late adjustment,
 * which needs ages), and the tax-free cash those pensions pay out.
 */
function getPensionsSummary(
  pensions: Record<string, AdditionalPensionData[]> | undefined,
  personIndex: number,
): { count: number; total: number; taxFreeCash: number } {
  const items = pensions?.[`person${personIndex}`] ?? [];
  return {
    count: items.length,
    total: items.reduce((sum, p) => sum + Math.max(0, p.annualAmount - (p.commutedAnnual ?? 0)), 0),
    taxFreeCash: items.reduce(
      (sum, p) => sum + (p.automaticLumpSum ?? 0) + (p.commutedAnnual ?? 0) * (p.commutationFactor ?? DEFAULT_COMMUTATION_FACTOR),
      0,
    ),
  };
}

function renderSupplementaryIncomeTable(
  formData: Partial<AllStepFormData>,
  toCalYear: (yr: number) => number | null,
): React.ReactNode {
  const income = formData.supplementaryIncome;
  if (!income || income.length === 0) return null;
  const maxYear = formData.projectionYears ?? 60;
  const rows = income.flatMap((item) => {
    const end = item.endYear ?? maxYear;
    return Array.from({ length: end - item.startYear + 1 }, (_, i) => ({
      key: `${item.incomeId}-${item.startYear + i}`,
      year: item.startYear + i,
      description: item.type === 'SALARY' ? `${item.description} (Salary)` : item.description,
      amount: item.grossAnnualAmount,
    }));
  }).sort((a, b) => a.year - b.year || a.description.localeCompare(b.description));
  return (
    <div className="review-table">
      <div className="review-table-header">
        <span>Year / Description</span>
        <span>Amount</span>
      </div>
      {rows.map((r) => (
        <div key={r.key} className="review-table-row">
          <span className="review-table-row-label">
            <span className="review-table-year">{toCalYear(r.year) ?? r.year}</span>
            {r.description}
          </span>
          <span className="review-table-amount">{fmt(r.amount)}/yr</span>
        </div>
      ))}
    </div>
  );
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  PRIMARY_RESIDENCE: 'Primary Residence',
  BUY_TO_LET: 'Buy-to-Let',
  HOLIDAY_LET: 'Holiday Let',
};

/** Extract properties array from form data, handling both legacy and new formats */
function getProperties(formData: Partial<AllStepFormData>): PropertyItem[] {
  if (formData.properties && formData.properties.length > 0) return formData.properties;
  if (formData.property) {
    return [{
      propertyId: 'legacy-0',
      label: 'Primary Residence',
      propertyType: 'PRIMARY_RESIDENCE',
      owner: 'joint',
      currentMarketValue: formData.property.currentMarketValue || 0,
      currentMortgageAmount: formData.property.currentMortgageAmount || 0,
      currentEquity: formData.property.currentEquity || 0,
      planningSale: formData.property.planningSale || false,
      plannedSaleDate: formData.property.plannedSaleDate || '',
      estateAgentFeePct: formData.property.estateAgentFeePct || 1.5,
      newPropertyPurchasePrice: formData.property.newPropertyPurchasePrice || 0,
      proceedsStrategy: (formData.property.proceedsStrategy || 'INVEST') as 'INVEST' | 'CASH',
      monthlyMortgagePayment: formData.property.monthlyMortgagePayment || 0,
      mortgageEndDate: formData.property.mortgageEndDate || '',
      purchasePrice: 0,
      acquisitionCosts: 0,
      improvementCosts: 0,
      interestOnlyMortgageAmount: 0,
      mortgageInterestRatePct: 0,
      legalFees: 0,
      rental: null,
    }];
  }
  return [];
}

function hasProperties(formData: Partial<AllStepFormData>): boolean {
  return getProperties(formData).length > 0;
}

function renderProperties(properties: PropertyItem[]): React.ReactNode {
  return properties.map((prop) => (
    <div key={prop.propertyId} className="review-subsection">
      <div className="review-subsection-title">
        {prop.label || 'Property'}
        <span className="review-strategy-badge" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>
          {PROPERTY_TYPE_LABELS[prop.propertyType] || prop.propertyType}
        </span>
      </div>
      <div className="review-grid">
        <Field label="Market Value" value={fmt(prop.currentMarketValue)} />
        <Field label="Mortgage" value={fmt(prop.currentMortgageAmount)} />
        <Field label="Equity" value={fmt(prop.currentEquity)} />
        {prop.propertyType !== 'PRIMARY_RESIDENCE' && (
          <>
            <Field label="Purchase Price" value={fmt(prop.purchasePrice)} />
            {prop.acquisitionCosts > 0 && <Field label="Purchase Costs" value={fmt(prop.acquisitionCosts)} />}
            {prop.improvementCosts > 0 && <Field label="Improvement Costs" value={fmt(prop.improvementCosts)} />}
          </>
        )}
        {(prop.currentMortgageAmount || 0) > 0 && (
          <>
            <Field label="Monthly Mortgage" value={fmt(prop.monthlyMortgagePayment)} />
            <Field label="Mortgage End" value={prop.mortgageEndDate || 'Until sale'} />
            <Field label="Interest Rate" value={pct(prop.mortgageInterestRatePct)} />
            {(prop.interestOnlyMortgageAmount || 0) > 0 && (
              <Field label="Interest-Only Portion" value={fmt(prop.interestOnlyMortgageAmount)} />
            )}
          </>
        )}
        {prop.rental && (
          <>
            <Field label="Rental Income" value={`${fmt(prop.rental.annualGrossIncome)}/yr`} />
            <Field label="Expenses" value={`${fmt(prop.rental.annualAllowableExpenses)}/yr`} />
            <Field label="Occupancy" value={pct(prop.rental.occupancyRatePct)} />
          </>
        )}
        {prop.planningSale && (
          <>
            <Field label="Planned Sale Date" value={prop.plannedSaleDate} />
            <Field label="Estate Agent Fee" value={pct(prop.estateAgentFeePct)} />
            {prop.propertyType === 'PRIMARY_RESIDENCE' && prop.newPropertyPurchasePrice > 0 && (
              <Field label="Downsize To" value={fmt(prop.newPropertyPurchasePrice)} />
            )}
            <Field label="Proceeds" value={prop.proceedsStrategy === 'CASH' ? 'Hold as cash' : 'Invest (brokerage)'} />
          </>
        )}
      </div>
    </div>
  ));
}

function StepReview({ formData, onEditStep, onViewProjection, onBack, isSubmitting, isAlternative = false, projectionReady = false, autoProjectionError = null }: Readonly<StepReviewProps>) {
  const persons = formData.persons ?? [];
  const isJoint = formData.planType === 'JOINT';
  const toCalYear = (yr: number) => formData.planAsOfDate ? planYearToCalendar(yr, formData.planAsOfDate) : null;

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const handleExportPlan = useCallback(() => {
    setExporting(true);
    setExportError(null);
    try {
      const exportData = {
        formData: stripLegacyFields({ ...formData } as Record<string, unknown>),
        exportedAt: new Date().toISOString(),
        exportedFrom: window.location.hostname,
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const planName = (formData.planName ?? 'plan').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
      const planType = isAlternative ? 'alternative' : 'primary';
      a.download = `${planName}-${planType}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [formData, isAlternative]);

  return (
    <div className="step-container">
      <div className="review-header">
        <div className="step-header-row">
          <h2>Review Your Plan</h2>
          <div className="step-header-actions">
            <button
              type="button"
              className="export-plan-btn"
              onClick={handleExportPlan}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export Plan'}
            </button>
            {exportError && <span className="export-plan-error">{exportError}</span>}
            <TourButton tour={WIZARD_STEP10_TOUR} variant="text" />
          </div>
        </div>
        <p>Check your inputs before creating the projection. Click Edit to change any section.</p>
      </div>

      <div className="review-container">
        {/* Plan Basics */}
        <SectionCard title="Plan Basics" step={1} onEdit={() => onEditStep(1)} dataTour="review-first-section">
          <div className="review-grid">
            <Field label="Plan Name" value={formData.planName} />
            <Field label="Plan Type" value={isJoint ? 'Joint' : 'Single'} />
            <Field label="As-of Date" value={formData.planAsOfDate} />
          </div>
        </SectionCard>

        {/* People */}
        <SectionCard title="People" step={2} onEdit={() => onEditStep(2)}>
          {(() => {
            // A plan starting 1 Jan-5 April bands its prior-activity seed into a
            // tax year the engine doesn't model (see calculatePriorTaxYearIncome
            // in initializationEngine.ts) — the figures are discarded entirely.
            // Annotate rather than hide: the user's answer is still there (and
            // still editable via Step 2), but a raw echo of a number the engine
            // silently ignores would be actively misleading.
            const priorActivityApplies = priorIncomeAppliesToPlanYear(formData.planAsOfDate);
            const notAppliedNote = (
              <span className="review-value--muted"> (not counted — received before this plan&apos;s tax year starts)</span>
            );
            return persons.map((person, i) => {
              // Derived, not read directly (phase 3, derived-total
              // refactor): once Step 2 has been visited since the
              // salary/drawdown/State Pension/rental split, `priorOtherTaxableIncome`
              // is no longer persisted on the plan at all — reading it
              // directly would silently show £0 for a plan that genuinely
              // has prior income recorded via the breakdown. Falls back to
              // the legacy bare total for a plan that predates the split
              // entirely. See deriveTotalPriorOtherIncome.
              const nonDividendTotal = deriveTotalPriorOtherIncome(
                person.priorSalaryIncome,
                person.priorDrawdownIncome,
                person.priorStatePensionIncome,
                person.priorRentalIncome,
                person.priorOtherTaxableIncome
              );
              return (
              <div key={person.fullName || `person-${i}`} className="review-subsection">
                <div className="review-subsection-title">{person.fullName || `Person ${i + 1}`}</div>
                <div className="review-grid">
                  <Field label="Date of Birth" value={person.dateOfBirth} />
                  <Field label="Retirement Date" value={person.retirementDate} />
                  <Field label="State Pension Age" value={person.statePensionAge} />
                  <Field label="State Pension (annual)" value={fmt(person.currentStatePensionAnnual)} />
                  <Field label="Take as Tax-Free Cash %" value={pct(person.taxFreeLumpSumPct)} />
                  <Field label="Lifetime Tax-Free Cash Limit" value={fmt(person.taxFreeLumpSumCap)} />
                  <Field label="Tax-Free Cash Already Taken" value={person.lsaUsedPrePlan ? fmt(person.lsaUsedPrePlan) : '£0 (none taken)'} />
                  {nonDividendTotal > 0 && (
                    <Field
                      label="Non-Dividend Income Already Received This Tax Year"
                      value={<>{fmt(nonDividendTotal)}{!priorActivityApplies && notAppliedNote}</>}
                    />
                  )}
                  {(person.priorDividendIncome ?? 0) > 0 && (
                    <Field
                      label="Dividend Income Already Received This Tax Year"
                      value={<>{fmt(person.priorDividendIncome)}{!priorActivityApplies && notAppliedNote}</>}
                    />
                  )}
                  {(person.priorPensionContributions ?? 0) > 0 && (
                    <Field
                      label="Pension Contributions Already Made This Tax Year"
                      value={<>{fmt(person.priorPensionContributions)}{!priorActivityApplies && notAppliedNote}</>}
                    />
                  )}
                </div>
              </div>
              );
            });
          })()}
        </SectionCard>

        {/* Investments */}
        <SectionCard title="Investments" step={3} onEdit={() => onEditStep(3)}>
          {persons.map((person, i) => {
            const h = getHoldingsSummary(formData.holdings, i);
            const p = getPensionsSummary(formData.additionalPensions, i);
            return (
              <div key={person.fullName || `person-${i}`} className="review-subsection">
                <div className="review-subsection-title">{person.fullName || `Person ${i + 1}`}</div>
                <ul className="review-list">
                  <li className="review-list-item">
                    <span className="review-list-item-label">SIPP</span>
                    <span className="review-list-item-value">{fmt(h.sipp)}</span>
                  </li>
                  <li className="review-list-item">
                    <span className="review-list-item-label">ISA</span>
                    <span className="review-list-item-value">{fmt(h.isa)}</span>
                  </li>
                  <li className="review-list-item">
                    <span className="review-list-item-label">GIA (Brokerage)</span>
                    <span className="review-list-item-value">{fmt(h.gia)}</span>
                  </li>
                  {h.individualBondCount > 0 && (
                    <li className="review-list-item">
                      <span className="review-list-item-label">Individual bonds (held to maturity)</span>
                      <span className="review-list-item-value">{h.individualBondCount}</span>
                    </li>
                  )}
                  {p.count > 0 && (
                    <li className="review-list-item">
                      <span className="review-list-item-label">Additional Pensions ({p.count})</span>
                      <span className="review-list-item-value">{fmt(p.total)}/yr</span>
                    </li>
                  )}
                  {p.taxFreeCash > 0 && (
                    <li className="review-list-item">
                      <span className="review-list-item-label">Pension tax-free cash</span>
                      <span className="review-list-item-value">{fmt(p.taxFreeCash)}</span>
                    </li>
                  )}
                  {(() => {
                    const c = formData.contributions?.[`person${i}`];
                    if (!c?.makingContributions) return null;
                    const monthlyGross = c.monthlyPersonalContribution * 1.25;
                    const annualTotal = (monthlyGross + (c.includeEmployer ? c.monthlyEmployerContribution : 0)) * 12;
                    return (
                      <>
                        <li className="review-list-item">
                          <span className="review-list-item-label">Pension Contributions</span>
                          <span className="review-list-item-value">{fmt(annualTotal)}/yr (gross)</span>
                        </li>
                        {c.annualIsaContribution > 0 && (
                          <li className="review-list-item">
                            <span className="review-list-item-label">ISA Contributions</span>
                            <span className="review-list-item-value">{fmt(c.annualIsaContribution)}/yr</span>
                          </li>
                        )}
                      </>
                    );
                  })()}
                </ul>
              </div>
            );
          })}
        </SectionCard>

        {/* Property */}
        <SectionCard title="Property" step={4} onEdit={() => onEditStep(4)}>
          {formData.includeProperty && hasProperties(formData) ? (
            renderProperties(getProperties(formData))
          ) : (
            <p className="review-value--muted">Not included</p>
          )}
        </SectionCard>

        {/* Supplementary Income */}
        <SectionCard title="Supplementary Income" step={5} onEdit={() => onEditStep(5)}>
          {(formData.supplementaryIncome?.length ?? 0) > 0
            ? renderSupplementaryIncomeTable(formData, toCalYear)
            : <p className="review-value--muted">None</p>}
        </SectionCard>

        {/* Spending */}
        <SectionCard title="Spending" step={6} onEdit={() => onEditStep(6)}>
          {formData.spendingPhases ? (
            <>
              {formData.spendingPhases.map((phase) => {
                const total = phase.livingExpenses + phase.travelLeisure + phase.otherDiscretionary;
                return (
                  <div key={phase.phaseId} className="review-subsection">
                    <div className="review-subsection-title">{phase.label}</div>
                    <div className="review-grid">
                      <Field label="Years" value={
                        phase.endYear != null
                          ? (toCalYear(phase.startYear) === null
                              ? `Yr ${phase.startYear}\u2013${phase.endYear}`
                              : `Yr ${phase.startYear}\u2013${phase.endYear} (${toCalYear(phase.startYear)}\u2013${toCalYear(phase.endYear)})`)
                          : (toCalYear(phase.startYear) === null
                              ? `Yr ${phase.startYear} onwards`
                              : `Yr ${phase.startYear} onwards (from ${toCalYear(phase.startYear)})`)
                      } />
                      <Field label="Total Annual" value={fmt(total)} />
                      <Field label="Living" value={fmt(phase.livingExpenses)} />
                      <Field label="Travel & Leisure" value={fmt(phase.travelLeisure)} />
                      <Field label="Other" value={fmt(phase.otherDiscretionary)} />
                    </div>
                  </div>
                );
              })}
              {(formData.oneTimeExpenses?.length ?? 0) > 0 && (
                <div className="review-subsection">
                  <div className="review-subsection-title">One-Time Expenses</div>
                  <div className="review-table">
                    <div className="review-table-header">
                      <span>Year / Description</span>
                      <span>Amount</span>
                    </div>
                    {[...formData.oneTimeExpenses!].sort((a, b) => a.year - b.year || a.amount - b.amount).map((e) => (
                      <div key={e.expenseId} className="review-table-row">
                        <span className="review-table-row-label">
                          <span className="review-table-year">{toCalYear(e.year) ?? e.year}</span>
                          {e.description}
                        </span>
                        <span className="review-table-amount">{fmt(e.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(formData.recurringExpenses?.length ?? 0) > 0 && (
                <div className="review-subsection">
                  <div className="review-subsection-title">Recurring Expenses</div>
                  <ul className="review-list">
                    {formData.recurringExpenses!.map((e) => (
                      <li key={e.expenseId} className="review-list-item">
                        <span className="review-list-item-label">
                          {e.description} [Yr {e.startYear}{e.endYear ? `\u2013${e.endYear}` : '+'}
                          {toCalYear(e.startYear) !== null && (
                            <> ({toCalYear(e.startYear)}{e.endYear ? `\u2013${toCalYear(e.endYear)}` : '+'})</>
                          )}]
                        </span>
                        <span className="review-list-item-value">{fmt(e.amountPerYear)}/yr</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <p className="review-value--muted">No spending phases configured</p>
          )}
        </SectionCard>

        {/* Later Life & Estate */}
        <SectionCard title="Later Life & Estate" step={7} onEdit={() => onEditStep(7)}>
          <div className="review-grid">
            {formData.personDeceasedAges?.map((pda) => {
              const name = persons[pda.personIndex]?.fullName || `Person ${pda.personIndex + 1}`;
              return (
                <Field
                  key={pda.personIndex}
                  label={`${name} Deceased Age`}
                  value={pda.deceasedAge ?? 'Not set'}
                />
              );
            })}
            <Field label="Survivor Spending" value={pct(formData.survivorSpendingPct)} />
            {/* Shown with the switch off too when the plan already carries a value:
                the switch only stops new input, it never hides data the engine applies. */}
            {(isFeatureEnabled('cashReserves') || formData.careFund) && (<>
            <Field label="Care Fund" value={formData.careFund ? fmt(formData.careFund) : 'Not set'} />
            <Field
              label="Care Fund Backstop Age"
              value={(() => {
                if (!formData.careFund) return 'Not set';
                if (formData.careFundReleaseAge) return formData.careFundReleaseAge;
                // Release happens on a care cost, on abandonment, or at the
                // backstop. With no care modelled and no backstop, nothing can
                // release it — promising otherwise would be untrue.
                return (formData.careScenarios?.length ?? 0) > 0
                  ? 'No backstop — released as care is paid for'
                  : 'Ring-fenced for the whole plan';
              })()}
            />
            </>)}
            <Field
              label="Direct Descendants"
              value={(() => {
                if (formData.hasDirectDescendants == null) return 'Not answered';
                return formData.hasDirectDescendants ? 'Yes' : 'No';
              })()}
            />
            <Field
              label="Pensions in Estate"
              value={(() => {
                if (formData.includePensionsInEstate == null) return 'Not answered';
                return formData.includePensionsInEstate ? 'Yes' : 'No';
              })()}
            />
          </div>
          {(formData.careScenarios?.length ?? 0) > 0 && (
            <div className="review-subsection">
              <div className="review-subsection-title">Care Scenarios</div>
              <ul className="review-list">
                {formData.careScenarios!.map((cs) => {
                  const name = persons[cs.personIndex]?.fullName || `Person ${cs.personIndex + 1}`;
                  return (
                    <li key={cs.scenarioId} className="review-list-item">
                      <span className="review-list-item-label">
                        {name} &mdash; {CARE_TYPE_LABELS[cs.careType] ?? cs.careType} [Yr {cs.startYear}{toCalYear(cs.startYear) !== null && <> ({toCalYear(cs.startYear)})</>}, {cs.durationYears}yr]
                      </span>
                      <span className="review-list-item-value">{fmt(cs.annualCost)}/yr</span>
                      {cs.sellPrimaryResidenceOnCareStart && (() => {
                        const properties = getProperties(formData);
                        const primary = properties.find(p => p.propertyType === 'PRIMARY_RESIDENCE');
                        if (!primary) {
                          return (
                            <div className="review-list-item-detail">
                              <em>No primary residence configured — add one in Step 4 to see an estimate.</em>
                            </div>
                          );
                        }
                        const inflationPct = formData.economicAssumptions?.inflationAnnualPct ?? 2.5;
                        const inflationRate = inflationPct / 100;
                        const isDownsizing = primary.planningSale && (primary.newPropertyPurchasePrice ?? 0) > 0;
                        const baseValue = isDownsizing ? primary.newPropertyPurchasePrice : primary.currentMarketValue;
                        // For downsizing: inflate from the downsizing year, not year 0
                        const downsizingYear = isDownsizing && primary.plannedSaleDate
                          ? Math.max(1, new Date(primary.plannedSaleDate).getFullYear() - new Date(formData.planAsOfDate || '2025-01-01').getFullYear() + 1)
                          : 0;
                        const inflationYears = cs.startYear - downsizingYear;
                        const agentFeePct = primary.estateAgentFeePct ?? 1.5;
                        const projectedValue = Math.round(baseValue * Math.pow(1 + inflationRate, inflationYears));
                        const agentFees = Math.round(projectedValue * agentFeePct / 100);
                        const netProceeds = projectedValue - agentFees;

                        return (
                          <div className="review-list-item-detail">
                            <strong>Home sale when care begins &mdash; estimated proceeds: {fmt(netProceeds)}</strong>
                            <div>
                              Based on {isDownsizing ? 'the new property' : 'current home'} value
                              of {fmt(baseValue)}, grown at {inflationPct}% per year
                              for {inflationYears} years, minus {agentFeePct}% selling costs.
                            </div>
                            <div>
                              Proceeds will be {cs.careSaleProceedsStrategy === 'INVEST' ? 'added to your investment portfolio' : 'held in cash to fund care'}.
                            </div>
                          </div>
                        );
                      })()}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {formData.ihtStrategies && hasAnyIHTStrategy(formData.ihtStrategies) && (
            <div className="review-subsection">
              <div className="review-subsection-title">IHT Strategies</div>
              <ul className="review-list">
                {formData.ihtStrategies.annualGiftExemption && (
                  <li className="review-list-item">
                    <span className="review-list-item-label">Annual Gift Exemption</span>
                    <span className="review-list-item-value">Enabled</span>
                  </li>
                )}
                {formData.ihtStrategies.smallGiftRecipientsPerYear > 0 && (
                  <li className="review-list-item">
                    <span className="review-list-item-label">Small Gift Recipients</span>
                    <span className="review-list-item-value">{formData.ihtStrategies.smallGiftRecipientsPerYear}/yr</span>
                  </li>
                )}
                {formData.ihtStrategies.charitableLegacyEnabled && (
                  <li className="review-list-item">
                    <span className="review-list-item-label">Charitable Legacy (36% rate)</span>
                    <span className="review-list-item-value">Enabled</span>
                  </li>
                )}
                {formData.ihtStrategies.normalExpenditureFromIncome && (
                  <li className="review-list-item">
                    <span className="review-list-item-label">Normal Expenditure from Income</span>
                    <span className="review-list-item-value">Enabled</span>
                  </li>
                )}
                {formData.ihtStrategies.annualPetAmount > 0 && (
                  <li className="review-list-item">
                    <span className="review-list-item-label">Annual PET</span>
                    <span className="review-list-item-value">{fmt(formData.ihtStrategies.annualPetAmount)}/yr</span>
                  </li>
                )}
                {formData.ihtStrategies.annualCharitableDonation > 0 && (
                  <li className="review-list-item">
                    <span className="review-list-item-label">Charitable Donation</span>
                    <span className="review-list-item-value">{fmt(formData.ihtStrategies.annualCharitableDonation)}/yr</span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </SectionCard>

        {/* Assumptions */}
        <SectionCard title="Assumptions" step={8} onEdit={() => onEditStep(8)}>
          <div className="review-grid">
            <Field label="Household Cash" value={fmt(formData.householdCash?.jointAccountBalance)} />
            <Field label="Equity Return" value={pct(formData.economicAssumptions?.equityReturnAnnualPct)} />
            <Field label="Bond Return" value={pct(formData.economicAssumptions?.bondReturnAnnualPct)} />
            <Field label="Inflation" value={pct(formData.economicAssumptions?.inflationAnnualPct)} />
            <Field label="Tax Band Inflation" value={pct(formData.economicAssumptions?.taxBandInflationPct)} />
            <Field label="State Pension Growth Add" value={pct(formData.economicAssumptions?.statePensionGrowthAddPct)} />
            <Field label="Dividend Yield" value={pct(formData.economicAssumptions?.dividendYieldPct)} />
          </div>
        </SectionCard>

        {/* Strategy */}
        <SectionCard title="Strategy" step={9} onEdit={() => onEditStep(9)}>
          <div className="review-grid">
            <Field
              label="Decumulation Strategy"
              value={
                <span className="review-strategy-badge">
                  {STRATEGY_LABELS[formData.decumulationStrategy ?? ''] ?? formData.decumulationStrategy}
                </span>
              }
            />
            <Field label="Projection Years" value={formData.projectionYears} />
            <Field label="ISA Annual Allowance" value={fmt(formData.isaAnnualAllowance)} />
            {/* As for the care fund: existing data stays visible with the switch off. */}
            {(isFeatureEnabled('cashReserves') || formData.emergencyFund) && (
            <Field label="Emergency Fund" value={formData.emergencyFund ? fmt(formData.emergencyFund) : 'Not set'} />
            )}
            {formData.decumulationStrategy === 'TAX_OPTIMISED' && (
              <>
                <Field
                  label="ISA Sheltering Mode"
                  value={ISA_SHELTERING_LABELS[formData.isaShelteringMode ?? ''] ?? formData.isaShelteringMode}
                />
                <Field
                  label="SIPP Band-Fill Mode"
                  value={BAND_FILL_LABELS[formData.bandFillMode ?? ''] ?? formData.bandFillMode}
                />
              </>
            )}
            {(() => {
              const purchases = Object.entries(formData.annuityPurchases ?? {})
                .flatMap(([key, rows]) => (rows ?? []).map(row => ({ key, row })));
              if (purchases.length === 0) return null;
              const multiple = persons.length > 1;
              return purchases.map(({ key, row }) => {
                const personIndex = Number.parseInt(key.replace('person', ''), 10);
                const name = persons[personIndex]?.fullName || `Person ${personIndex + 1}`;
                return (
                  <Field
                    key={`${key}-${row.purchaseId}`}
                    label={multiple ? `Annuity (${name})` : 'Annuity'}
                    value={summariseAnnuityPurchase(row)}
                    full
                  />
                );
              });
            })()}
            <Field
              label="Investment Rebalancing"
              value={(() => {
                if (formData.timeHorizonRebalancing) return 'Time-Horizon';
                const custom = formData.allocationOverridePct;
                const resolved = custom ?? blendedEquityPctFromHoldings(formData.holdings) ?? undefined;
                if (resolved == null) return 'Fixed Allocation';
                return `Fixed Allocation (${Math.round(resolved * 100)}% equity, ${custom != null ? 'custom' : 'from your holdings'})`;
              })()}
            />
          </div>
        </SectionCard>

        {/* Projection error */}
        {autoProjectionError && !projectionReady && (
          <div className="projection-error" role="alert">
            <p>Projection could not be generated. Please go back and check your inputs:</p>
            <ul className="projection-error-list">
              {autoProjectionError.split('; ').map((msg) => {
                const stepMatch = /Go back to Step (\d+)/i.exec(msg);
                if (stepMatch) {
                  const stepNum = Number(stepMatch[1]);
                  const textBefore = msg.slice(0, stepMatch.index);
                  const textAfter = msg.slice(stepMatch.index + stepMatch[0].length);
                  return (
                    <li key={msg}>
                      {textBefore}
                      <button type="button" className="projection-error-step-link" onClick={() => onEditStep(stepNum)}>
                        Go to Step {stepNum}
                      </button>
                      {textAfter}
                    </li>
                  );
                }
                return <li key={msg}>{msg}</li>;
              })}
            </ul>
          </div>
        )}

        {/* Submit */}
        <div className="review-submit">
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary btn-large"
            onClick={onViewProjection}
            disabled={!projectionReady || isSubmitting}
          >
            {projectionReady
              ? (isAlternative ? 'Compare Plans' : 'View Projection')
              : autoProjectionError
                ? 'Projection Failed'
                : <><span className="spinner" aria-hidden="true" /> Generating projection...</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

function hasAnyIHTStrategy(s: { annualGiftExemption: boolean; smallGiftRecipientsPerYear: number; charitableLegacyEnabled: boolean; normalExpenditureFromIncome: boolean; annualPetAmount: number; annualCharitableDonation: number }): boolean {
  return s.annualGiftExemption ||
    s.smallGiftRecipientsPerYear > 0 ||
    s.charitableLegacyEnabled ||
    s.normalExpenditureFromIncome ||
    s.annualPetAmount > 0 ||
    s.annualCharitableDonation > 0;
}

export default StepReview;
